import { useEffect } from "react";
import { recoverMessageAddress, type Hex } from "viem";
import { useAppDispatch } from "../app/hooks";
import config from "../config/config";
import { AUTH_TOKEN_HEADER } from "../constants/const";
import {
  clearAuth,
  requestAuthorize,
  setAuth,
} from "@/features/auth";

export const checkSignature = async (account: string) => {
  const storedToken =
    localStorage.getItem(`${AUTH_TOKEN_HEADER}${account.toLowerCase()}`) ??
    localStorage.getItem(`${AUTH_TOKEN_HEADER}${account}`);
  const storedSignature = storedToken?.split("_")[0];
  const expireAt = parseInt(storedToken?.split("_")[1] || "0");

  if (storedSignature) {
    const signMsg = `Sign in ${config.domain} with ${account} - expire at ${expireAt}`;

    try {
      const signerAddress = await recoverMessageAddress({
        message: JSON.stringify(signMsg),
        signature: storedSignature as Hex,
      });

      if (signerAddress.toLowerCase() === account.toLowerCase()) {
        if (expireAt > new Date().getTime() / 1000) {
          return false;
        } else {
          return true;
        }
      } else {
        return true;
      }
    } catch {
      return true;
    }
  } else {
    return true;
  }
};

const useAuth = (account: string) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!account) {
      dispatch(clearAuth());
      return;
    }

    // Never expose credentials from the previously connected account while
    // the new account's stored token is being checked.
    dispatch(clearAuth());

    let cancelled = false;

    const authorize = async () => {
      const needsAuth = await checkSignature(account);
      if (cancelled) return;

      if (needsAuth) {
        dispatch(requestAuthorize({ account }));
      } else {
        dispatch(setAuth(account));
      }
    };

    authorize();

    return () => {
      cancelled = true;
    };
  }, [account, dispatch]);
};

export default useAuth;
